import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, CreditCard, Link2, Loader2, SkipForward, X } from 'lucide-react';
import { api } from '@/lib/api';
import { fmt } from '@/lib/liquidaciones';
import { classNames } from '@/lib/utils';

/**
 * Pagar con Qonto varias liquidaciones seleccionadas en el listado.
 *
 * Primero un resumen con cada propietario, su IBAN, el importe y el total, y
 * qué se va a saltar y por qué. Después se va de una en una: el PDF de la
 * liquidación a la izquierda y «Pagar» a la derecha, así cada transferencia se
 * mira antes de mandarla pero sin abrir cada liquidación. Cada pago es la
 * misma llamada que el paso 7, con el id de la liquidación como clave de
 * idempotencia: Qonto no la paga dos veces aunque se repita.
 */

type Fase = 'resumen' | 'pagando' | 'fin';
type Resultado = { estado: 'pagada' | 'saltada' | 'error'; detalle?: string; importe?: number };

const fmtIban = (iban: string) => iban.replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim();
const conceptoPorDefecto = (liq: any) => `Liquidación ${liq.periodo} - ${liq.ownerName}`.slice(0, 140);
const esNoConectado = (msg: string) => /no conectado/i.test(msg);

/** Por qué una liquidación no se paga, o null si se puede pagar. */
function motivoNoPago(liq: any, owner: any): string | null {
  const st = liq.paymentStatus || 'none';
  if (st === 'completed') return 'Ya pagada';
  if (st === 'processing' || st === 'pending') return 'Transferencia en proceso';
  if (!(Number(liq.result?.saldo) > 0)) return 'Saldo a favor de Sereno: no hay nada que pagar';
  if (!owner?.iban) return 'El propietario no tiene IBAN';
  return null;
}

export default function PagoMasivo({
  liquidaciones,
  owners,
  generarPdf,
  onLiquidacionActualizada,
  onCerrar,
}: {
  liquidaciones: any[];
  owners: any[];
  /** El PDF de la liquidación o factura, el mismo de «Descargar» */
  generarPdf: (liq: any) => Blob;
  onLiquidacionActualizada: (liq: any) => void;
  onCerrar: () => void;
}) {
  const filas = useMemo(() => liquidaciones.map(liq => {
    const owner = owners.find(o => o.id === liq.ownerId);
    return { liq, owner, motivo: motivoNoPago(liq, owner), importe: Math.round((Number(liq.result?.saldo) || 0) * 100) / 100 };
  }), [liquidaciones, owners]);
  const aPagar = filas.filter(f => !f.motivo);
  const totalAPagar = aPagar.reduce((s, f) => s + f.importe, 0);

  const [fase, setFase] = useState<Fase>('resumen');
  const [qonto, setQonto] = useState<'comprobando' | 'conectado' | 'desconectado' | 'error'>('comprobando');
  const [indice, setIndice] = useState(0);
  const [concepto, setConcepto] = useState('');
  const [pagando, setPagando] = useState(false);
  const [errorActual, setErrorActual] = useState('');
  const [resultados, setResultados] = useState<Record<string, Resultado>>({});

  async function comprobarQonto() {
    setQonto('comprobando');
    try {
      const r = await api.qontoEstado();
      setQonto(r.connected ? 'conectado' : 'desconectado');
    } catch {
      setQonto('error');
    }
  }
  useEffect(() => { comprobarQonto(); }, []);

  /** Se abre desde un clic, para que el navegador no bloquee la ventana */
  function conectarQonto() {
    const popup = window.open('/api/qonto/auth', 'qonto-auth', 'width=620,height=720,left=200,top=80');
    if (!popup) {
      setErrorActual('El navegador ha bloqueado la ventana de Qonto. Permite ventanas emergentes para el CRM.');
      return;
    }
    const t = setInterval(() => {
      if (popup.closed) { clearInterval(t); comprobarQonto(); setErrorActual(''); }
    }, 500);
  }

  const actual = fase === 'pagando' ? aPagar[indice] : null;

  // El concepto y el PDF cambian con cada liquidación
  useEffect(() => {
    if (actual) { setConcepto(conceptoPorDefecto(actual.liq)); setErrorActual(''); }
  }, [actual?.liq.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [pdf, setPdf] = useState<{ url: string | null; error: string }>({ url: null, error: '' });
  useEffect(() => {
    if (!actual) return;
    let url: string | null = null;
    try {
      url = URL.createObjectURL(generarPdf(actual.liq));
      setPdf({ url, error: '' });
    } catch (err: any) {
      setPdf({ url: null, error: err?.message || 'No se pudo generar el PDF' });
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [actual?.liq.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function avanzar(id: string, r: Resultado) {
    setResultados(prev => ({ ...prev, [id]: r }));
    if (indice + 1 < aPagar.length) setIndice(indice + 1);
    else setFase('fin');
  }

  async function pagarActual() {
    if (!actual) return;
    setPagando(true);
    setErrorActual('');
    try {
      const r: any = await api.payLiquidacion(actual.liq.id, concepto.trim() || conceptoPorDefecto(actual.liq));
      if (r?.liquidacion) onLiquidacionActualizada(r.liquidacion);
      avanzar(actual.liq.id, { estado: 'pagada', importe: actual.importe });
    } catch (err: any) {
      const msg = err?.message || 'Error al procesar el pago';
      if (esNoConectado(msg)) setQonto('desconectado');
      setErrorActual(msg);
    } finally {
      setPagando(false);
    }
  }

  function saltarActual() {
    if (!actual) return;
    avanzar(actual.liq.id, errorActual ? { estado: 'error', detalle: errorActual } : { estado: 'saltada' });
  }

  const pagadas = Object.values(resultados).filter(r => r.estado === 'pagada');
  const totalPagado = pagadas.reduce((s, r) => s + (r.importe || 0), 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={classNames('bg-white rounded-2xl shadow-xl w-full max-h-[94vh] flex flex-col', fase === 'pagando' ? 'max-w-6xl' : 'max-w-3xl')}>
        {/* Cabecera */}
        <div className="flex items-start justify-between gap-3 p-6 pb-4 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-sereno-dark">Pagar con Qonto</h3>
            <p className="text-sm text-sereno-dark/60 mt-1">
              {fase === 'resumen' && 'Revisa a quién se paga y cuánto. Después irás de una en una, viendo cada liquidación antes de pagarla.'}
              {fase === 'pagando' && `Liquidación ${indice + 1} de ${aPagar.length} · pagado hasta ahora ${fmt(totalPagado)} de ${fmt(totalAPagar)}`}
              {fase === 'fin' && 'Resumen de lo que se ha hecho.'}
            </p>
          </div>
          {!pagando && (
            <button onClick={onCerrar} className="text-sereno-dark/40 hover:text-sereno-dark" title={fase === 'pagando' ? 'Parar aquí' : 'Cerrar'}><X size={20} /></button>
          )}
        </div>

        {/* Fase 1: resumen */}
        {fase === 'resumen' && (
          <>
            <div className="overflow-y-auto divide-y divide-gray-100">
              {filas.map(f => (
                <div key={f.liq.id} className={classNames('flex items-center gap-3 px-6 py-3 text-sm', f.motivo && 'opacity-60')}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sereno-dark truncate">{f.liq.ownerName} <span className="font-normal text-sereno-dark/50">· {f.liq.periodo}</span></p>
                    <p className="text-xs text-sereno-dark/60 font-mono truncate">{f.owner?.iban ? fmtIban(f.owner.iban) : '—'}</p>
                    {f.motivo && <p className="text-xs text-amber-700">{f.motivo}: se salta</p>}
                  </div>
                  <span className={classNames('tabular-nums font-semibold shrink-0', f.motivo ? 'text-sereno-dark/40 line-through' : 'text-sereno-dark')}>{fmt(f.importe)}</span>
                </div>
              ))}
            </div>
            <div className="p-6 pt-4 border-t border-gray-100 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-sereno-dark/60">{aPagar.length} de {filas.length} se pagarán</span>
                <span className="text-base font-bold text-sereno-dark tabular-nums">Total {fmt(totalAPagar)}</span>
              </div>
              {qonto === 'desconectado' && (
                <div className="flex items-center justify-between gap-3 text-sm rounded-lg border p-3 bg-amber-50 border-amber-200 text-amber-800">
                  <span className="flex items-center gap-2"><AlertTriangle size={16} /> Qonto no está conectado.</span>
                  <button onClick={conectarQonto} className="btn-ghost text-sm flex items-center gap-2"><Link2 size={14} /> Conectar con Qonto</button>
                </div>
              )}
              {qonto === 'error' && <p className="text-sm text-red-600">No se pudo comprobar la conexión con Qonto. <button className="underline" onClick={comprobarQonto}>Reintentar</button></p>}
              {errorActual && <p className="text-sm text-red-600">{errorActual}</p>}
              <div className="flex justify-end gap-3">
                <button onClick={onCerrar} className="btn-ghost">Cancelar</button>
                <button
                  onClick={() => { setIndice(0); setFase('pagando'); }}
                  disabled={aPagar.length === 0 || qonto !== 'conectado'}
                  className="btn-primary flex items-center gap-2"
                >
                  {qonto === 'comprobando' ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />}
                  Empezar · {aPagar.length} pago{aPagar.length === 1 ? '' : 's'}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Fase 2: una a una, con la liquidación a la vista */}
        {fase === 'pagando' && actual && (
          <div className="flex flex-col lg:flex-row min-h-0 flex-1">
            <div className="lg:flex-1 bg-gray-50 border-b lg:border-b-0 lg:border-r border-gray-100 min-h-[50vh]">
              {pdf.error
                ? <p className="p-6 text-sm text-red-600">{pdf.error}</p>
                : pdf.url && <iframe key={pdf.url} title="Liquidación" src={pdf.url} className="w-full h-full min-h-[50vh]" />}
            </div>
            <div className="lg:w-96 p-6 flex flex-col gap-4">
              <div>
                <p className="text-xs font-semibold text-sereno-dark/60 uppercase tracking-wide">Propietario</p>
                <p className="font-semibold text-sereno-dark">{actual.liq.ownerName}</p>
                <p className="text-sm text-sereno-dark/60">{actual.liq.periodo}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-sereno-dark/60 uppercase tracking-wide">IBAN</p>
                <p className="font-mono text-sm text-sereno-dark">{fmtIban(actual.owner.iban)}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-sereno-dark/60 uppercase tracking-wide">Importe</p>
                <p className="text-3xl font-bold text-sereno-dark tabular-nums">{fmt(actual.importe)}</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-sereno-dark/60 uppercase tracking-wide mb-1.5">Concepto</label>
                <input className="input text-sm" value={concepto} maxLength={140} onChange={e => setConcepto(e.target.value)} />
              </div>

              {errorActual && (
                <div className="flex items-start gap-2 text-sm rounded-lg border p-3 bg-red-50 border-red-200 text-red-700">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" /> <span>{errorActual}</span>
                </div>
              )}
              {qonto === 'desconectado' && (
                <button onClick={conectarQonto} className="btn-ghost text-sm flex items-center gap-2 self-start"><Link2 size={14} /> Conectar con Qonto</button>
              )}

              <div className="mt-auto flex items-center gap-3 pt-2">
                <button onClick={saltarActual} disabled={pagando} className="btn-ghost flex items-center gap-2">
                  <SkipForward size={16} /> Saltar
                </button>
                <button
                  onClick={pagarActual}
                  disabled={pagando || qonto !== 'conectado'}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-50"
                >
                  {pagando ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />}
                  {pagando ? 'Pagando…' : errorActual ? 'Reintentar' : `Pagar ${fmt(actual.importe)}`}
                </button>
              </div>
              <p className="text-xs text-sereno-dark/40">Al pagar, Qonto envía la transferencia y pasa a la siguiente.</p>
            </div>
          </div>
        )}

        {/* Fase 3: resultado */}
        {fase === 'fin' && (
          <>
            <div className="overflow-y-auto divide-y divide-gray-100">
              {filas.map(f => {
                const r = resultados[f.liq.id];
                const etiqueta = f.motivo ? f.motivo : !r ? 'Sin tocar' : r.estado === 'pagada' ? 'Transferencia enviada' : r.estado === 'saltada' ? 'Saltada' : `Error: ${r.detalle}`;
                return (
                  <div key={f.liq.id} className="flex items-center gap-3 px-6 py-3 text-sm">
                    {r?.estado === 'pagada'
                      ? <CheckCircle2 size={16} className="text-green-600 shrink-0" />
                      : r?.estado === 'error' ? <AlertTriangle size={16} className="text-red-600 shrink-0" /> : <SkipForward size={16} className="text-sereno-dark/30 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sereno-dark truncate">{f.liq.ownerName} <span className="font-normal text-sereno-dark/50">· {f.liq.periodo}</span></p>
                      <p className={classNames('text-xs', r?.estado === 'error' ? 'text-red-600' : 'text-sereno-dark/50')}>{etiqueta}</p>
                    </div>
                    <span className="tabular-nums text-sereno-dark/70">{fmt(f.importe)}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between gap-3 p-6 pt-4 border-t border-gray-100">
              <p className="text-sm text-sereno-dark">
                <strong>{pagadas.length}</strong> transferencia{pagadas.length === 1 ? '' : 's'} enviada{pagadas.length === 1 ? '' : 's'} · <strong>{fmt(totalPagado)}</strong>
              </p>
              <button onClick={onCerrar} className="btn-primary">Cerrar</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
